import { useEffect, useMemo, useRef, useState } from "react";
import { differenceInYears, format } from "date-fns";
import { ru } from "date-fns/locale";
import { useGetClientCoursesQuery } from "@/queries/useGetClientCoursesQuery";
import { useGetClientQuery } from "@/queries/useGetClientQuery";
import { useGetCourseClassesQuery } from "@/queries/useGetCourseClassesQuery";
import { useGetCoursesQuery } from "@/queries/useGetCoursesQuery";
import { useGetTicketsQuery } from "@/queries/useGetTicketsQuery";
import type { Course, CourseClass } from "@/@types/courses.types";
import type { PackageTicket, Ticket } from "@/@types/tickets.types";
import { parseApiDate } from "@/utils/dateFormatters";

export type AgeBucketId = "all" | "lt1" | "1_3" | "3_5" | "gt5";

export interface CoursesFilters {
  ageBucket: AgeBucketId;
}

const DEFAULT_FILTERS: CoursesFilters = {
  ageBucket: "all",
};

export interface AgeBucket {
  id: AgeBucketId;
  label: string;
  from: number; // включительно
  to: number; // исключительно (Infinity = без верха)
}

export interface DateGroup {
  key: string;
  date: Date;
  /** "27 апреля" */
  dateLabel: string;
  /** "понедельник" */
  dayOfWeekLabel: string;
  items: WeekClassItem[];
}

export interface WeekClassItem {
  cls: CourseClass;
  course: Course;
}

export const AGE_BUCKETS: AgeBucket[] = [
  { id: "lt1", label: "до 12 мес", from: 0, to: 1 },
  { id: "1_3", label: "1 – 3 года", from: 1, to: 3 },
  { id: "3_5", label: "3 – 5 лет", from: 3, to: 5 },
  { id: "gt5", label: "> 5 лет", from: 5, to: Infinity },
];

const computeAgeFromBirthday = (birthday: string | null | undefined): number | null => {
  if (!birthday) return null;
  const date = new Date(birthday);
  if (Number.isNaN(date.getTime())) return null;
  const age = differenceInYears(new Date(), date);
  return age >= 0 ? age : null;
};

// Курс относим к бакету по его нижней границе возраста (from).
// Отсекаем мусор: age.id === null и битые from===0 && to===0 (встречаются в 1С как заглушка).
const courseBucket = (course: Course): AgeBucketId | null => {
  const a = course.age;
  if (!a.id) return null;
  if ((a.from ?? 0) === 0 && (a.to ?? 0) === 0) {
    return null;
  }
  const from = a.from ?? 0;
  for (const b of AGE_BUCKETS) {
    if (from >= b.from && from < b.to) return b.id;
  }
  return null;
};

const findBucketForAge = (userAge: number | null): AgeBucketId | null => {
  if (userAge == null) return null;
  const b = AGE_BUCKETS.find((x) => userAge >= x.from && userAge < x.to);
  return b ? b.id : null;
};

// Ищем среди абонементов пользователя подходящий для записи на групповое занятие.
// Критерии: package, service_list содержит «группов» (magic string — 1С не отдаёт
// семантичный флаг, так что regexp — единственный способ), clubId есть в
// available_clubs, status === "active" (неактивированный не запишет).
const pickGroupTicketId = (tickets: Ticket[] | undefined, clubId: string | null): string | null => {
  if (!tickets || !clubId) return null;
  const candidate = tickets.find((t): t is PackageTicket => {
    if (t.type !== "package" && t.type !== "membership") return false;
    if (t.status !== "active") return false;
    const pkg = t as PackageTicket;
    const hasGroup = pkg.service_list?.some((s) => /группов/i.test(s.title));
    if (!hasGroup) return false;
    const clubOk = !pkg.available_clubs?.length || pkg.available_clubs.some((c) => c.id === clubId);
    return !!clubOk;
  });
  return candidate ? candidate.ticket_id : null;
};

export const useCoursesList = (clubId: string | null, trainerId: string = "all") => {
  const { data: courses = [], isLoading, isError } = useGetCoursesQuery(clubId);
  const { data: clientCourses = [] } = useGetClientCoursesQuery();
  const { data: clientData } = useGetClientQuery();
  const { data: classes = [] } = useGetCourseClassesQuery(clubId);
  const { data: tickets } = useGetTicketsQuery();

  const groupTicketId = useMemo(() => pickGroupTicketId(tickets, clubId), [tickets, clubId]);

  const [filters, setFilters] = useState<CoursesFilters>(DEFAULT_FILTERS);
  const didApplyDefaultBucketForClub = useRef<string | null>(null);

  // При смене клуба сбрасываем автобакет, иначе юзер может остаться с фильтром
  // из предыдущего клуба, в котором в новом клубе нет секций.
  useEffect(() => {
    if (didApplyDefaultBucketForClub.current !== clubId) {
      didApplyDefaultBucketForClub.current = null;
      setFilters(DEFAULT_FILTERS);
    }
  }, [clubId]);

  const clientCourseIds = useMemo(() => new Set(clientCourses.map((c) => c.id)), [clientCourses]);

  const coursesWithBookingState = useMemo<Course[]>(
    () =>
      courses.map((course) =>
        clientCourseIds.has(course.id) ? { ...course, already_booked: true } : course
      ),
    [courses, clientCourseIds]
  );

  const bucketCounts = useMemo<Record<AgeBucketId, number>>(() => {
    const counts: Record<AgeBucketId, number> = {
      "all": coursesWithBookingState.length,
      "lt1": 0,
      "1_3": 0,
      "3_5": 0,
      "gt5": 0,
    };
    coursesWithBookingState.forEach((course) => {
      const b = courseBucket(course);
      if (b) counts[b] += 1;
    });
    return counts;
  }, [coursesWithBookingState]);

  useEffect(() => {
    if (didApplyDefaultBucketForClub.current === clubId) return;
    if (coursesWithBookingState.length === 0) return;
    const userAge = computeAgeFromBirthday(clientData?.birthday);
    if (userAge == null) return;
    const bucket = findBucketForAge(userAge);
    if (bucket && bucketCounts[bucket] > 0) {
      setFilters((prev) => ({ ...prev, ageBucket: bucket }));
    }
    didApplyDefaultBucketForClub.current = clubId;
  }, [clubId, coursesWithBookingState, clientData?.birthday, bucketCounts]);

  const filteredCourses = useMemo(() => {
    return coursesWithBookingState.filter((course) => {
      if (trainerId !== "all" && course.employee.id !== trainerId) {
        return false;
      }
      if (filters.ageBucket !== "all" && courseBucket(course) !== filters.ageBucket) {
        return false;
      }
      return true;
    });
  }, [coursesWithBookingState, filters.ageBucket, trainerId]);

  // Агрегация фактических занятий (/classes/) по датам.
  // Применяем те же фильтры (возраст, тренер), что и к секциям — через lookup course_id.
  // Занятия, где пользователь уже записан, показываем всегда (игнорируя фильтры),
  // чтобы клиент всегда видел свои записи.
  const filteredCourseIds = useMemo(
    () => new Set(filteredCourses.map((c) => c.id)),
    [filteredCourses]
  );
  const courseById = useMemo(() => {
    const map = new Map<string, Course>();
    coursesWithBookingState.forEach((c) => map.set(c.id, c));
    return map;
  }, [coursesWithBookingState]);

  const groupedByDate = useMemo<DateGroup[]>(() => {
    const now = Date.now();
    const days = new Map<string, DateGroup>();

    classes.forEach((cls) => {
      const cid = cls.course?.id;
      if (!cid) return;
      const course = courseById.get(cid);
      if (!course) return;
      const passesFilters = filteredCourseIds.has(cid);
      if (!cls.already_booked && !passesFilters) return;
      const start = parseApiDate(cls.start_date);
      if (!start || start.getTime() < now) return;
      const key = format(start, "yyyy-MM-dd");
      const group = days.get(key) ?? {
        key,
        date: start,
        dateLabel: format(start, "d MMMM", { locale: ru }),
        dayOfWeekLabel: format(start, "EEEE", { locale: ru }),
        items: [],
      };
      group.items.push({ cls, course });
      days.set(key, group);
    });

    return Array.from(days.values())
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .map((g) => ({
        ...g,
        items: g.items.sort(
          (a, b) =>
            (parseApiDate(a.cls.start_date)?.getTime() ?? 0) -
            (parseApiDate(b.cls.start_date)?.getTime() ?? 0)
        ),
      }));
  }, [classes, courseById, filteredCourseIds]);

  return {
    filteredCourses,
    groupedByDate,
    isLoading,
    isError,
    filters,
    setFilters,
    bucketCounts,
    groupTicketId,
  };
};
